import { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  Package,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useProductCategories,
  type ProductFilters,
  type Product,
} from '../hooks/useProducts';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface ProductsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const emptyForm = {
  name: '',
  category: '',
  carrier: '',
  coverageType: '',
  basePremium: '',
  maxPremium: '',
  commissionRate: '',
  description: '',
  active: true,
};

export function ProductsView({ onNavigate }: ProductsViewProps) {
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState(emptyForm);

  const filters: ProductFilters = {
    category: categoryFilter || undefined,
    carrier: carrierFilter || undefined,
    active: activeFilter ? activeFilter === 'true' : undefined,
    search: searchText || undefined,
    limit: 200,
  };

  const { data, isLoading, error } = useProducts(filters);
  const { data: categoriesData } = useProductCategories();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const products = data?.products ?? [];
  const categories = categoriesData?.categories ?? [];

  function openCreateForm() {
    setEditingProduct(null);
    setFormData(emptyForm);
    setShowForm(true);
  }

  function openEditForm(product: Product) {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      carrier: product.carrier ?? '',
      coverageType: product.coverageType ?? '',
      basePremium: product.basePremium?.toString() ?? '',
      maxPremium: product.maxPremium?.toString() ?? '',
      commissionRate: product.commissionRate?.toString() ?? '',
      description: product.description ?? '',
      active: product.active,
    });
    setShowForm(true);
  }

  function handleSave() {
    const payload: Partial<Product> = {
      name: formData.name,
      category: formData.category,
      carrier: formData.carrier || undefined,
      coverageType: formData.coverageType || undefined,
      basePremium: formData.basePremium ? Number(formData.basePremium) : undefined,
      maxPremium: formData.maxPremium ? Number(formData.maxPremium) : undefined,
      commissionRate: formData.commissionRate ? Number(formData.commissionRate) : undefined,
      description: formData.description || undefined,
      active: formData.active,
    };

    if (editingProduct) {
      updateProduct.mutate({ id: editingProduct.id, data: payload }, {
        onSuccess: () => setShowForm(false),
      });
    } else {
      createProduct.mutate(payload, {
        onSuccess: () => { setShowForm(false); setFormData(emptyForm); },
      });
    }
  }

  function handleDelete(product: Product) {
    deleteProduct.mutate(product.id);
    if (selectedProduct?.id === product.id) setSelectedProduct(null);
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Products</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} product${data.total !== 1 ? 's' : ''}` : 'Product catalog'}
          </p>
        </div>
        <button className="btn-primary" onClick={openCreateForm}>
          <Plus className="h-4 w-4" />
          New Product
        </button>
      </div>

      {/* Filter Bar */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search products..."
              className="input pl-9"
            />
          </form>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Category</label>
              <select className="input py-1.5" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="">All</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label mb-1">Active</label>
              <select className="input py-1.5" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as '' | 'true' | 'false')}>
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">Failed to load products.</div>
        ) : isLoading ? (
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card p-4 h-40 animate-pulse bg-surface-100" />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-3 gap-4">
            {products.map((product) => (
              <div
                key={product.id}
                className={cn(
                  'card p-4 cursor-pointer transition-shadow hover:shadow-md',
                  selectedProduct?.id === product.id && 'ring-2 ring-brand-500',
                )}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">{product.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{product.category}</p>
                  </div>
                  <Badge color={product.active ? 'green' : 'gray'} size="sm">
                    {product.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {product.carrier && <p className="text-xs text-slate-500 mt-2">Carrier: {product.carrier}</p>}
                {(product.basePremium || product.maxPremium) && (
                  <p className="text-xs text-slate-600 mt-1">
                    Premium: {product.basePremium ? formatCurrency(product.basePremium) : '--'}
                    {product.maxPremium ? ` - ${formatCurrency(product.maxPremium)}` : ''}
                  </p>
                )}
                {product.commissionRate !== undefined && (
                  <p className="text-xs text-slate-500 mt-1">Commission: {product.commissionRate}%</p>
                )}
                <div className="mt-3 flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-secondary btn-sm" onClick={() => openEditForm(product)}>
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button className="btn-secondary btn-sm text-red-600" onClick={() => handleDelete(product)}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Package}
            title="No products yet"
            description="Add products to your catalog."
            actionLabel="Add Product"
            onAction={openCreateForm}
          />
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{editingProduct ? 'Edit Product' : 'New Product'}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label mb-1">Name</label>
                <input className="input" value={formData.name} onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Category</label>
                <input className="input" value={formData.category} onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Carrier</label>
                <input className="input" value={formData.carrier} onChange={(e) => setFormData((f) => ({ ...f, carrier: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Coverage Type</label>
                <input className="input" value={formData.coverageType} onChange={(e) => setFormData((f) => ({ ...f, coverageType: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">Base Premium</label>
                  <input className="input" type="number" value={formData.basePremium} onChange={(e) => setFormData((f) => ({ ...f, basePremium: e.target.value }))} />
                </div>
                <div>
                  <label className="label mb-1">Max Premium</label>
                  <input className="input" type="number" value={formData.maxPremium} onChange={(e) => setFormData((f) => ({ ...f, maxPremium: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label mb-1">Commission Rate (%)</label>
                <input className="input" type="number" value={formData.commissionRate} onChange={(e) => setFormData((f) => ({ ...f, commissionRate: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Description</label>
                <textarea className="input" rows={3} value={formData.description} onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="product-active" checked={formData.active} onChange={(e) => setFormData((f) => ({ ...f, active: e.target.checked }))} />
                <label htmlFor="product-active" className="text-sm text-slate-700">Active</label>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={(createProduct.isPending || updateProduct.isPending) || !formData.name || !formData.category}
              >
                {(createProduct.isPending || updateProduct.isPending) ? 'Saving...' : editingProduct ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
